/**
 * Who may open what behind the Elevation gate (Wes, 2026-10-18).
 *
 * Three levels, keyed by Supabase auth user id (ids never change; the email
 * beside each is only a label):
 *
 *   owner     Wes alone. Elevation's own knowledge system: the pages in
 *             ELEVATION_OWNER_ONLY_PATHS below. It will move to a separate app.
 *   team      Wes and Harry. Everything else behind the gate: Design Language,
 *             the Lab's product and career tools, the mental-model pages,
 *             /world, the Method, the Workbook, the roadmap, the archive, and
 *             the team hub at /home.
 *   customer  Everyone else. Nothing behind the gate.
 *
 * Named in code rather than a permission, by Wes's choice. Do not add anyone's
 * other accounts: most of them are demo personas shown to prospects. Wes's
 * +admin account is the one exception: removed on 2026-10-18 because he did
 * not know it existed, put back on 2026-10-26 because it is the account he
 * signs in with ("Add wbright+admin@peakpriorities.com as Elevation owner").
 *
 * This decides what the app draws. It is not a security boundary: what any
 * account may read is still decided by the database.
 */

import { useUserStore } from '../stores/prioritization/userStore';
import { useSessionCheckPending } from './authSessionState';

const WES = '1d0a9656-497c-4e22-87f8-1e0763bdeed0'; // wbright@peakpriorities.com
const WES_ADMIN = '019f620b-ee89-435e-893d-e393af134270'; // wbright+admin@peakpriorities.com
const HARRY = '33ed1ee9-6a31-4591-b787-c4676e0af66e'; // hmax@peakpriorities.com

export const ELEVATION_OWNER_USER_IDS: readonly string[] = [WES, WES_ADMIN];
export const ELEVATION_TEAM_USER_IDS: readonly string[] = [WES, WES_ADMIN, HARRY];

/**
 * Elevation's own knowledge system: owner-only. Each entry also covers the
 * paths under it, on path-segment boundaries (`/elevation/design` covers
 * `/elevation/design/x`, never `/elevation/design-language`). Everything else
 * behind the gate is team.
 *
 * Source: Proposal 09, "Separating Elevation's knowledge system from
 * Priorities.ai" (~/Vault/_system/proposals/09-elevation-separation.md):
 * § 2 (the page groups), § 3 (Wes's marks on the Lab), "The four layers
 * belong to Elevation", and § 8.1 (Elevation's new home). When Wes moves a
 * page between Elevation and Priorities.ai, add or remove its line here.
 *
 * Not here, so team pages: the mental-model pages (Ladder of Inference and its
 * variants, Johari Window, Logical Levels, Inquiry Sequence, Bounded Agency,
 * Field of Play), Island and Mountain. They stay in Priorities.ai (Wes,
 * 2026-10-18: "All of this stays in priorities-ai too"; Proposal 09 § 8.2,
 * "Mental models").
 */
export const ELEVATION_OWNER_ONLY_PATHS: readonly string[] = [
  // Hub
  '/elevation/home', // Mission Control
  '/elevation/design', // the content-workflow overview
  '/elevation/about',
  '/elevation/site-map',
  '/elevation/wes-sandbox',
  // The four layers (M3 to M0) and the knowledge model
  '/elevation/grammar',
  '/elevation/models',
  '/elevation/templates',
  '/elevation/runtime',
  '/elevation/entry',
  '/elevation/enhanced',
  '/elevation/glossary',
  '/elevation/advanced-search',
  '/elevation/artifact-gallery',
  // The stages and the machinery
  '/elevation/app',
  '/elevation/workflow',
  '/elevation/engines',
  '/elevation/operations',
  '/elevation/architecture', // the page; the Lab's architecture tool is team
  '/elevation/notion-highlights',
  '/elevation/notion-intake',
  '/elevation/batch-process-images',
  '/elevation/status',
  '/elevation/studio',
  '/elevation/ai-navigator',
  '/elevation/canon-engine',
  '/elevation/orchestration-engine',
  '/elevation/definition/canon-engine',
  '/elevation/documentation-hub',
  '/elevation/documentation', // alias: redirects to documentation-hub
  '/elevation/lab/canon', // alias: redirects to canon-engine
  // Personal
  '/elevation/progress',
  '/elevation/workshops',
  '/elevation/outcomes',
  '/elevation/analytics',
  '/elevation/my-notes',
  '/elevation/team',
  // Lab tools that move with Elevation (Wes's marks, Proposal 09 § 3)
  '/elevation/lab/consistency',
  '/elevation/lab/folder-creator',
  '/elevation/lab/youtube-search',
  '/elevation/lab/m2-model-taxonomy',
];

export type ElevationLevel = 'owner' | 'team';
export type ElevationAccess = ElevationLevel | 'customer';

export function elevationAccessFor(userId: string | null | undefined): ElevationAccess {
  if (typeof userId !== 'string') return 'customer';
  if (ELEVATION_OWNER_USER_IDS.includes(userId)) return 'owner';
  if (ELEVATION_TEAM_USER_IDS.includes(userId)) return 'team';
  return 'customer';
}

export function isElevationOwner(userId: string | null | undefined): boolean {
  return elevationAccessFor(userId) === 'owner';
}

/** The owner is on the team too. */
export function isElevationTeam(userId: string | null | undefined): boolean {
  return elevationAccessFor(userId) !== 'customer';
}

/** Whether someone with `access` may open a page that needs `level`. */
export function hasElevationLevel(access: ElevationAccess, level: ElevationLevel): boolean {
  if (level === 'owner') return access === 'owner';
  return access === 'owner' || access === 'team';
}

/**
 * The address as the router matches it: each segment percent-decoded and the
 * whole lower-cased (routes match case-insensitively), so `/elevation/HOME`
 * and `/elevation/h%6Fme` are read as the owner page they open. Design
 * Language reads its addresses the same way (features/design-language).
 */
export function routerPath(pathname: string): string {
  let decoded = pathname;
  try {
    decoded = pathname
      .split('/')
      .map((segment) => decodeURIComponent(segment).replace(/\//g, '%2F'))
      .join('/');
  } catch {
    // Malformed percent-encoding: the router leaves it as it is, so do we.
  }
  return decoded.toLowerCase();
}

/** True for the pages in ELEVATION_OWNER_ONLY_PATHS and every path under them. */
export function isElevationOwnerOnlyPath(pathname: string): boolean {
  const path = routerPath(pathname);
  return ELEVATION_OWNER_ONLY_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * True for a team page with a product address under the team hub, such as the
 * product roadmap at /home/roadmap (Proposal 09 § 8.2, "Product records").
 * Such a page is behind the same gate as the team pages under /elevation and
 * gets the same bar and frame; the hub itself, /home, draws its own page.
 */
export function isTeamHubSubpage(pathname: string): boolean {
  return /^\/home\/[^/]/.test(routerPath(pathname));
}

/** The level a path behind the Elevation gate needs. */
export function elevationLevelForPath(pathname: string): ElevationLevel {
  return isElevationOwnerOnlyPath(pathname) ? 'owner' : 'team';
}

/**
 * The signed-in person's level, read the way `RequireAuth` reads who is
 * signed in: the user store's `userId`, after `AuthSessionSync`'s first
 * session check. While that check is pending the store may still hold the
 * previous person from localStorage, so the answer is `pending`, never a level.
 */
export function useElevationAccess(): ElevationAccess | 'pending' {
  const userId = useUserStore((s) => s.userId);
  const sessionCheckPending = useSessionCheckPending();
  if (sessionCheckPending) return 'pending';
  return elevationAccessFor(userId);
}

/** True only once it is known the signed-in person is the owner. */
export function useIsElevationOwner(): boolean {
  return useElevationAccess() === 'owner';
}

/** True only once it is known the signed-in person is on the team (owner included). */
export function useIsElevationTeam(): boolean {
  const access = useElevationAccess();
  return access === 'owner' || access === 'team';
}
